import { useState, useEffect, useRef, useCallback } from "react";
import clsx from "clsx";

import {
  asyncDataLoaderFeature,
  hotkeysCoreFeature,
} from "@headless-tree/core";
import { useTree } from "@headless-tree/react";

import { DefaultSidebar, Sidebar } from "@excalidraw/excalidraw";
import {
  ExplorerIcon,
  PlusIcon,
  LoadIcon,
  DeviceDesktopIcon,
  save,
  ExportImageIcon,
  boltIcon,
  searchIcon,
  HelpIcon,
  eyeIcon,
  loginIcon,
  settingsIcon,
  SunIcon,
  MoonIcon,
  checkIcon,
  gridIcon,
  wrenchIcon,
  EmbedIcon,
  LassoIcon,
  mermaidLogoIcon,
  MagicIcon,
  createIcon,
  FreedrawIcon,
  ImageIcon,
  CloseIcon,
} from "@excalidraw/excalidraw/components/icons";

import { isDevEnv, THEME, isPromiseLike } from "@excalidraw/common";

import { useI18n, languages } from "@excalidraw/excalidraw/i18n";

import {
  CaptureUpdateAction,
  showSelectedShapeActions,
} from "@excalidraw/element";

import { isExcalidrawPlusSignedUser } from "../app_constants";
import { useSetAtom } from "../app-jotai";
import { appLangCodeAtom } from "../app-language/language-state";

import {
  useExcalidrawActionManager,
  useExcalidrawAppState,
  useExplorerPropertiesPortal,
  useExcalidrawSetAppState,
  useAppProps,
  useApp,
} from "../../packages/excalidraw/components/App";
import { loadFromBlob } from "../../packages/excalidraw/data";
import { ImageSceneDataError } from "../../packages/excalidraw/errors";
import {
  SelectedShapeActions,
  ShapesSwitcher,
} from "../../packages/excalidraw/components/Actions";
import { LockButton } from "../../packages/excalidraw/components/LockButton";
import {
  actionLoadScene,
  actionSaveFileToDisk,
  actionToggleSearchMenu,
  actionShortcuts,
  actionClearCanvas,
  actionToggleObjectsSnapMode,
  actionToggleArrowBinding,
  actionToggleMidpointSnapping,
  actionToggleGridMode,
  actionToggleZenMode,
  actionToggleStats,
} from "../../packages/excalidraw/actions";
import { actionToggleViewMode } from "../../packages/excalidraw/actions/actionToggleViewMode";

import {
  LISTA_TREE_SNAPSHOT_VERSION,
  loadListaTreeSnapshot,
  saveListaTreeSnapshot,
} from "../data/listaTreeStorage";

import { saveDebugState } from "./DebugCanvas";
import { MiniaturePanel } from "./MiniaturePanel";

import "./AppSidebar.scss";

import type { AppState } from "../../packages/excalidraw/types";
import type { DevicePreviewMode } from "../useHandleDevicePreview";
import type { ListaTreeSnapshot } from "../data/listaTreeStorage";

const devicePreviewIconProps = {
  width: 24,
  height: 24,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const DevicePhoneIcon = createIcon(
  <g>
    <rect x="6.5" y="2.5" width="11" height="19" rx="2" />
    <path d="M10 19.5h4" />
  </g>,
  devicePreviewIconProps,
);

const DeviceTabletIcon = createIcon(
  <g>
    <rect x="4" y="2.5" width="16" height="19" rx="2" />
    <path d="M10 19.5h4" />
  </g>,
  devicePreviewIconProps,
);

const ListIcon = createIcon(
  <>
    <path d="M3 5h8" />
    <path d="M3 12h8" />
    <path d="M3 19h8" />
    <path d="m15 8 3-3 3 3" />
    <path d="m15 16 3 3 3-3" />
  </>,
  {
    width: 24,
    height: 24,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 0.5,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  },
);

const listaTreeIconProps = {
  width: 24,
  height: 24,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const ListaFolderIcon = createIcon(
  <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />,
  listaTreeIconProps,
);

const ListaAddFolderIcon = createIcon(
  <>
    <path d="M12 10v6" />
    <path d="M9 13h6" />
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  </>,
  { ...listaTreeIconProps, strokeWidth: 1.2 },
);

const ListaRefreshFolderIcon = createIcon(
  <>
    <path d="M9 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v.5" />
    <path d="M12 10v4h4" />
    <path d="m12 14 1.535-1.605a5 5 0 0 1 8 1.5" />
    <path d="M22 22v-4h-4" />
    <path d="m22 18-1.535 1.605a5 5 0 0 1-8-1.5" />
  </>,
  { ...listaTreeIconProps, strokeWidth: 1.2 },
);

const ListaFolderOpenIcon = createIcon(
  <>
    <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
  </>,
  listaTreeIconProps,
);

const ListaFileIcon = createIcon(
  <>
    <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
    <path d="M14 2v5a1 1 0 0 0 1 1h5" />
  </>,
  listaTreeIconProps,
);

const FilePdfIcon = createIcon(
  <>
    <rect x="1" y="5" width="22" height="14" rx="2.5" fill="currentColor" stroke="none" />
    <text x="12" y="12" fill="#ffffff" stroke="none" fontSize="9.5" fontFamily="system-ui, sans-serif" textAnchor="middle" dominantBaseline="central">
      PDF
    </text>
  </>,
  listaTreeIconProps,
);

const FilePngIcon = createIcon(
  <>
    <rect x="1" y="5" width="22" height="14" rx="2.5" fill="currentColor" stroke="none" />
    <text x="12" y="12" fill="#ffffff" stroke="none" fontSize="9.25" fontFamily="system-ui, sans-serif" textAnchor="middle" dominantBaseline="central">
      PNG
    </text>
  </>,
  listaTreeIconProps,
);

const FileSvgIcon = createIcon(
  <>
    <rect x="1" y="5" width="22" height="14" rx="2.5" fill="currentColor" stroke="none" />
    <text x="12" y="12" fill="#ffffff" stroke="none" fontSize="9.25" fontFamily="system-ui, sans-serif" textAnchor="middle" dominantBaseline="central">
      SVG
    </text>
  </>,
  listaTreeIconProps,
);

const getListaTreeDisplayName = (name: string) => {
  const lowerName = name.toLowerCase();

  for (const suffix of [
    ".onepaper.png",
    ".excalidraw.png",
    ".onepaper.svg",
    ".excalidraw.svg",
  ]) {
    if (lowerName.endsWith(suffix)) {
      return name.slice(0, -suffix.length);
    }
  }

  if (isListaSceneFile(lowerName)) {
    return name.replace(/\.(onepaper|excalidraw)$/i, "");
  }

  return name.replace(/\.[^/.]+$/, "");
};

const isListaImageFile = (lowerName: string) =>
  lowerName.endsWith(".png") ||
  lowerName.endsWith(".jpg") ||
  lowerName.endsWith(".jpeg") ||
  lowerName.endsWith(".svg") ||
  lowerName.endsWith(".gif") ||
  lowerName.endsWith(".webp");

const isListaSceneFile = (lowerName: string) =>
  lowerName.endsWith(".onepaper") || lowerName.endsWith(".excalidraw");

interface Item {
  name: string;
  children?: string[];
  handle?: any; // FileSystemDirectoryHandle | FileSystemFileHandle
  isLoaded?: boolean;
}

const initialTreeItems: Record<string, Item> = {
  root: {
    name: "OnePaper",
    children: [],
    isLoaded: true,
  },
};

const indent = 20;

export const AppSidebar: React.FC<{
  refresh?: () => void;
  devicePreviewMode: DevicePreviewMode;
  onDevicePreviewModeChange: (mode: DevicePreviewMode) => void;
}> = ({ refresh, devicePreviewMode, onDevicePreviewModeChange }) => {
  const [activePanel, setActivePanel] = useState<"explorer" | "lista" | null>(
    null,
  );

  const [treeItems, setTreeItems] =
    useState<Record<string, Item>>(initialTreeItems);
  const treeItemsRef = useRef(treeItems);
  const [pendingListaTreeSnapshot, setPendingListaTreeSnapshot] =
    useState<ListaTreeSnapshot | null>(null);
  const [isListaTreeHydrated, setIsListaTreeHydrated] = useState(false);
  const [isListaTreeRestored, setIsListaTreeRestored] = useState(false);

  // Sincroniza o estado atualizado com a referência que o headless-tree lê
  useEffect(() => {
    treeItemsRef.current = treeItems;
  }, [treeItems]);

  const tree = useTree<Item>({
    initialState: {
      expandedItems: ["root"],
    },
    indent,
    rootItemId: "root",
    getItemName: (item) =>
      item.getItemData()?.name ?? item.getId().split("/").at(-1) ?? "",
    isItemFolder: (item) => {
      const data = item.getItemData();
      return data?.children !== undefined;
    },
    dataLoader: {
      getItem: (itemId) => treeItemsRef.current[itemId],
      getChildren: async (itemId) => {
        const item = treeItemsRef.current[itemId];
        if (!item) {
          return [];
        }

        if (item.handle?.kind === "directory" && !item.isLoaded) {
          try {
            const newChildrenIds: string[] = [];
            const newItems: Record<string, Item> = {};

            for await (const [name, handle] of (item.handle as any).entries()) {
              if (handle.kind === "file") {
                const nameLower = name.toLowerCase();
                const isValidFile =
                  nameLower.endsWith(".onepaper") ||
                  nameLower.endsWith(".excalidraw") ||
                  nameLower.endsWith(".onepaper.png") ||
                  nameLower.endsWith(".excalidraw.png") ||
                  nameLower.endsWith(".onepaper.svg") ||
                  nameLower.endsWith(".excalidraw.svg") ||
                  nameLower.endsWith(".pdf") ||
                  nameLower.endsWith(".png") ||
                  nameLower.endsWith(".jpg") ||
                  nameLower.endsWith(".jpeg") ||
                  nameLower.endsWith(".svg") ||
                  nameLower.endsWith(".webp") ||
                  nameLower.endsWith(".gif");

                if (!isValidFile) {
                  continue;
                }
              }

              const childId = `${itemId}/${name}`;
              newItems[childId] = {
                name,
                handle,
                children: handle.kind === "directory" ? [] : undefined,
                isLoaded: false,
              };
              newChildrenIds.push(childId);
            }

            newChildrenIds.sort((a, b) => {
              const itemA = newItems[a];
              const itemB = newItems[b];
              if (itemA.handle.kind === itemB.handle.kind) {
                return itemA.name.localeCompare(itemB.name);
              }
              return itemA.handle.kind === "directory" ? -1 : 1;
            });

            const nextState = {
              ...treeItemsRef.current,
              ...newItems,
              [itemId]: {
                ...treeItemsRef.current[itemId],
                children: newChildrenIds,
                isLoaded: true,
              },
            };

            // Atualiza sincronicamente a ref para o headless-tree encontrar os itens logo em seguida
            treeItemsRef.current = nextState;
            setTreeItems(nextState);

            return newChildrenIds;
          } catch (e) {
            console.error("Error reading directory", e);
            return [];
          }
        }
        return item.children ?? [];
      },
    },
    features: [asyncDataLoaderFeature, hotkeysCoreFeature],
  });

  const syncListaTreeRoot = useCallback(
    (childrenIds?: string[]) => {
      const rootItem = tree.getItemInstance("root");
      rootItem.updateCachedData(treeItemsRef.current.root, true);
      rootItem.updateCachedChildrenIds(
        childrenIds ?? treeItemsRef.current.root.children ?? [],
      );
    },
    [tree],
  );

  useEffect(() => {
    let cancelled = false;

    const hydrateListaTree = async () => {
      const snapshot = await loadListaTreeSnapshot();

      if (cancelled) {
        return;
      }

      if (snapshot) {
        const restoredTreeItems = snapshot.treeItems as Record<string, Item>;
        treeItemsRef.current = restoredTreeItems;
        setTreeItems(restoredTreeItems);
        setActivePanel(snapshot.activePanel);
        syncListaTreeRoot(restoredTreeItems.root.children ?? []);
        setPendingListaTreeSnapshot(snapshot);
      }

      setIsListaTreeHydrated(true);
    };

    void hydrateListaTree();

    return () => {
      cancelled = true;
    };
  }, [syncListaTreeRoot]);

  const collectListaTreeDirectories = (
    itemId: string,
    visited: Set<string>,
    nextState: Record<string, Item>,
    directoryIds: string[],
  ) => {
    if (visited.has(itemId)) {
      return;
    }

    visited.add(itemId);

    const item = nextState[itemId];
    if (!item?.handle || item.handle.kind !== "directory") {
      return;
    }

    directoryIds.push(itemId);
    nextState[itemId] = { ...item, isLoaded: false };

    const childIds = item.children ?? [];
    for (const childId of childIds) {
      collectListaTreeDirectories(childId, visited, nextState, directoryIds);
    }
  };

  const refreshListaTree = async () => {
    const nextState = { ...treeItemsRef.current };
    const visited = new Set<string>();
    const directoryIds: string[] = [];

    for (const childId of treeItemsRef.current.root.children ?? []) {
      collectListaTreeDirectories(childId, visited, nextState, directoryIds);
    }

    treeItemsRef.current = nextState;
    setTreeItems(nextState);
    syncListaTreeRoot(nextState.root.children ?? []);

    for (const itemId of directoryIds) {
      await tree.getItemInstance(itemId).invalidateChildrenIds(true);
    }
  };

  const removeListaTreeFolder = useCallback(
    (folderId: string) => {
      if (folderId === "root") {
        return;
      }

      const currentItems = treeItemsRef.current;
      const folder = currentItems[folderId];
      if (!folder?.handle || folder.handle.kind !== "directory") {
        return;
      }

      const idsToRemove = new Set<string>();
      const visit = (itemId: string) => {
        if (idsToRemove.has(itemId)) {
          return;
        }

        idsToRemove.add(itemId);

        for (const childId of currentItems[itemId]?.children ?? []) {
          visit(childId);
        }
      };

      visit(folderId);

      const nextState: Record<string, Item> = {};
      for (const [itemId, item] of Object.entries(currentItems)) {
        if (idsToRemove.has(itemId)) {
          continue;
        }

        nextState[itemId] = {
          ...item,
          children: item.children?.filter((childId) => !idsToRemove.has(childId)),
        };
      }

      treeItemsRef.current = nextState;
      setTreeItems(nextState);
      syncListaTreeRoot(nextState.root.children ?? []);
    },
    [syncListaTreeRoot],
  );

  // Notificar a árvore de que os dados mudaram para que ela se reconstrua
  useEffect(() => {
    if (tree) {
      tree.rebuildTree();
    }
  }, [treeItems, tree]);

  const expandedItems = tree.getState().expandedItems;
  const buildListaTreeSnapshot = useCallback(
    (): ListaTreeSnapshot => ({
      version: LISTA_TREE_SNAPSHOT_VERSION,
      activePanel,
      expandedItems: [...expandedItems],
      treeItems: Object.fromEntries(
        Object.entries(treeItemsRef.current).map(([itemId, item]) => [
          itemId,
          {
            ...item,
            children: item.children ? [...item.children] : item.children,
          },
        ]),
      ),
    }),
    [activePanel, expandedItems],
  );

  useEffect(() => {
    if (!isListaTreeHydrated) {
      return;
    }

    if (!pendingListaTreeSnapshot) {
      setIsListaTreeRestored(true);
      return;
    }

    syncListaTreeRoot(pendingListaTreeSnapshot.treeItems.root.children ?? []);

    for (const itemId of pendingListaTreeSnapshot.expandedItems) {
      if (itemId === "root") {
        continue;
      }

      const item = treeItemsRef.current[itemId];
      if (item?.handle?.kind === "directory") {
        tree.getItemInstance(itemId).expand();
      }
    }

    setPendingListaTreeSnapshot(null);
    setIsListaTreeRestored(true);
  }, [isListaTreeHydrated, pendingListaTreeSnapshot, syncListaTreeRoot, tree]);

  useEffect(() => {
    if (!isListaTreeRestored) {
      return;
    }

    const timeout = window.setTimeout(() => {
      void saveListaTreeSnapshot(buildListaTreeSnapshot());
    }, 300);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [buildListaTreeSnapshot, isListaTreeRestored]);

  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isDevicePreviewMenuOpen, setIsDevicePreviewMenuOpen] = useState(false);
  const [isExtraToolsOpen, setIsExtraToolsOpen] = useState(false);
  const previousHasSelectionRef = useRef(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const preferencesRef = useRef<HTMLDivElement>(null);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const devicePreviewMenuRef = useRef<HTMLDivElement>(null);
  const extraToolsRef = useRef<HTMLDivElement>(null);
  const explorerPropertiesPortal = useExplorerPropertiesPortal();
  const actionManager = useExcalidrawActionManager();
  const fullAppState = useExcalidrawAppState();
  const setAppState = useExcalidrawSetAppState();
  const appProps = useAppProps();
  const appState = fullAppState;
  const app = useApp();
  const { t, langCode } = useI18n();
  const setLangCode = useSetAtom(appLangCodeAtom);
  const explorerPanelRef = useRef<HTMLDivElement | null>(null);
  const shouldShowPropertiesPanel = showSelectedShapeActions(
    appState,
    actionManager.getElementsIncludingDeleted() as any,
  );
  const [forceMenu, setForceMenu] = useState(false);
  const forceMenuRef = useRef(forceMenu);
  forceMenuRef.current = forceMenu;

  const activeToolCustomType = appState.activeTool.customType;
  const selectedElementsKey = Object.keys(appState.selectedElementIds).join(
    ",",
  );
  const getDevicePreviewLabel = (mode: DevicePreviewMode) => {
    if (mode === "tablet") {
      return "Tablet";
    }

    if (mode === "phone") {
      return "Celular";
    }

    return "Computador";
  };

  useEffect(() => {
    setForceMenu(false);
  }, [selectedElementsKey]);

  const isPropertiesPanelOpen = shouldShowPropertiesPanel && !forceMenu;
  const isExplorerPanelOpen = isPropertiesPanelOpen || activePanel !== null;
  const isExplorerViewActive =
    isPropertiesPanelOpen || activePanel === "explorer";
  const isListaViewActive = activePanel === "lista";

  useEffect(() => {
    if (devicePreviewMode === "phone" && activePanel === "lista") {
      setActivePanel(null);
    }
  }, [devicePreviewMode, activePanel]);

  useEffect(() => {
    if (shouldShowPropertiesPanel && !previousHasSelectionRef.current) {
      setActivePanel("explorer");
      setForceMenu(false);
    }

    if (!shouldShowPropertiesPanel && previousHasSelectionRef.current) {
      if (!forceMenuRef.current) {
        setActivePanel(null);
      }
      setForceMenu(false);
    }

    previousHasSelectionRef.current = shouldShowPropertiesPanel;
  }, [shouldShowPropertiesPanel]);

  useEffect(() => {
    // Multiple menus can be open at once, so closing rules live in one place.
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (target.closest(".onepaper-activity-bar__shapes")) {
        setActivePanel(null);
        setForceMenu(true);

        if (
          activeToolCustomType === "papel" &&
          !target.closest("[data-testid='toolbar-canvas-tools']")
        ) {
          app.setActiveTool({ type: "selection" });
        }
        return;
      }

      if (isPropertiesPanelOpen) {
        return;
      }

      if (
        activePanel !== null &&
        sidebarRef.current &&
        !sidebarRef.current.contains(target) &&
        !target.closest(
          "[data-prevent-outside-click], [data-radix-popper-content-wrapper], .excalidraw-eye-dropper-backdrop, .excalidraw-eye-dropper-container",
        )
      ) {
        setActivePanel(null);
      }

      if (
        isPreferencesOpen &&
        preferencesRef.current &&
        !preferencesRef.current.contains(event.target as Node)
      ) {
        setIsPreferencesOpen(false);
      }

      if (
        isLanguageMenuOpen &&
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target as Node)
      ) {
        setIsLanguageMenuOpen(false);
      }

      if (
        isDevicePreviewMenuOpen &&
        devicePreviewMenuRef.current &&
        !devicePreviewMenuRef.current.contains(event.target as Node)
      ) {
        setIsDevicePreviewMenuOpen(false);
      }

      if (
        isExtraToolsOpen &&
        extraToolsRef.current &&
        !extraToolsRef.current.contains(event.target as Node)
      ) {
        setIsExtraToolsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isPropertiesPanelOpen) {
        return;
      }

      if (event.key === "Escape") {
        if (isPreferencesOpen) {
          setIsPreferencesOpen(false);
        } else if (isExtraToolsOpen) {
          setIsExtraToolsOpen(false);
        } else if (isLanguageMenuOpen) {
          setIsLanguageMenuOpen(false);
        } else if (isDevicePreviewMenuOpen) {
          setIsDevicePreviewMenuOpen(false);
        } else if (activePanel !== null) {
          setActivePanel(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isPropertiesPanelOpen,
    forceMenu,
    activePanel,
    isPreferencesOpen,
    isExtraToolsOpen,
    isLanguageMenuOpen,
    isDevicePreviewMenuOpen,
    app,
    activeToolCustomType,
  ]);

  const explorerAppState: AppState = {
    ...appState,
    openPopup: explorerPropertiesPortal?.openPopup ?? null,
  };

  const renderExplorerAction: typeof actionManager.renderAction = (
    name,
    data,
  ) => {
    const canvasActions = appProps.UIOptions.canvasActions;

    if (
      actionManager.actions[name] &&
      "PanelComponent" in actionManager.actions[name] &&
      (name in canvasActions
        ? canvasActions[name as keyof typeof canvasActions]
        : true)
    ) {
      const action = actionManager.actions[name];
      const PanelComponent = action.PanelComponent!;
      PanelComponent.displayName = "PanelComponent";

      const updateData = (formState?: any) => {
        if (
          formState &&
          typeof formState === "object" &&
          "openPopup" in formState
        ) {
          explorerPropertiesPortal?.setOpenPopup(formState.openPopup ?? null);
          return;
        }

        const actionResult = action.perform(
          actionManager.getElementsIncludingDeleted(),
          explorerAppState,
          formState,
          app,
        );

        const applyResult = (result: Awaited<typeof actionResult>) => {
          if (!result) {
            return;
          }

          const nextResult = { ...result };

          if (nextResult.appState && "openPopup" in nextResult.appState) {
            const { openPopup, ...restAppState } = nextResult.appState;
            explorerPropertiesPortal?.setOpenPopup(openPopup ?? null);
            nextResult.appState = Object.keys(restAppState).length
              ? restAppState
              : null;
          }

          if (
            nextResult.elements ||
            nextResult.files ||
            nextResult.replaceFiles ||
            nextResult.appState
          ) {
            actionManager.updater(nextResult);
          }
        };

        if (isPromiseLike(actionResult)) {
          actionResult.then(applyResult);
        } else {
          applyResult(actionResult);
        }
      };

      return (
        <PanelComponent
          elements={actionManager.getElementsIncludingDeleted()}
          appState={explorerAppState}
          updateData={updateData}
          appProps={appProps}
          app={app}
          data={data}
          renderAction={renderExplorerAction}
        />
      );
    }

    return null;
  };

  const renderExplorerContent = () => {
    // The explorer is split into actions, developer helpers, preferences, language and canvas
    // settings so each section can close independently without duplicated logic.
    if (isPropertiesPanelOpen) {
      return (
        <div className="onepaper-explorer-panel__properties shape-actions-theme-scope properties-content">
          <SelectedShapeActions
            appState={explorerAppState}
            elementsMap={app.scene.getNonDeletedElementsMap()}
            renderAction={renderExplorerAction}
            app={app}
          />
        </div>
      );
    }

    if (isListaViewActive) {
      return (
        <div className="onepaper-list-tree" role="tree" aria-label="Lista">
          {tree.getItems().map((item, index, arr) => {
            const depth =
              (item as any).getItemMeta?.()?.level ??
              (item as any).getLevel?.() ??
              0;
            const isFolder = item.isFolder();
            const isExpanded = item.isExpanded();

            const linesToDraw: {
              d: number;
              top: number | string;
              bottom: number | string;
            }[] = [];

            for (let d = 0; d < depth; d++) {
              let hasSibling = false;
              for (let i = index + 1; i < arr.length; i++) {
                const nextDepth =
                  (arr[i] as any).getItemMeta?.()?.level ??
                  (arr[i] as any).getLevel?.() ??
                  0;
                if (nextDepth <= d) {
                  break;
                }
                if (nextDepth === d + 1) {
                  hasSibling = true;
                  break;
                }
              }

              if (depth === d + 1) {
                if (hasSibling) {
                  linesToDraw.push({ d, top: "-0.04rem", bottom: "-0.04rem" });
                } else {
                  linesToDraw.push({ d, top: "-0.04rem", bottom: "50%" });
                }
              } else if (hasSibling) {
                linesToDraw.push({ d, top: "-0.04rem", bottom: "-0.04rem" });
              }
            }

            if (isFolder && isExpanded) {
              const next = arr[index + 1];
              const hasChildren =
                next &&
                ((next as any).getItemMeta?.()?.level ??
                  (next as any).getLevel?.() ??
                  0) ===
                  depth + 1;
              if (hasChildren) {
                linesToDraw.push({
                  d: depth,
                  top: "calc(50% + 8px)",
                  bottom: "-0.04rem",
                });
              }
            }

            const itemData = item.getItemData() as Item | undefined;
            const fileHandleName = appState.fileHandle?.name?.toLowerCase();
            const itemHandleName = itemData?.handle?.name?.toLowerCase();
            const isActiveFile =
              !isFolder &&
              !!itemData?.handle &&
              (appState.fileHandle === itemData.handle ||
                (!!fileHandleName && fileHandleName === itemHandleName));

            return (
              <div key={item.getId()} className="onepaper-list-tree__node">
                <div className="onepaper-list-tree__rowShell">
                  <button
                    type="button"
                    className={clsx(
                      "onepaper-list-tree__row",
                      isFolder
                        ? "onepaper-list-tree__row--folder"
                        : "onepaper-list-tree__row--leaf",
                      isActiveFile && "onepaper-list-tree__row--active",
                    )}
                    style={{
                      paddingLeft: `calc(${depth} * 20px + 2px)`,
                    }}
                    aria-expanded={isFolder ? isExpanded : undefined}
                    aria-current={isActiveFile ? "true" : undefined}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const itemData = item.getItemData() as Item | undefined;
                      const liveItemData = treeItemsRef.current[item.getId()];
                      const fileHandle =
                        (typeof itemData?.handle?.getFile === "function"
                          ? itemData.handle
                          : liveItemData?.handle) as any;
                      const props = item.getProps();
                      props.onClick?.(e);

                      if (isFolder || e.ctrlKey || e.shiftKey || e.metaKey) {
                        return;
                      }

                      const clearResult = await actionManager.executeAction(
                        actionClearCanvas,
                      );

                      if (clearResult === false) {
                        return;
                      }

                      if (fileHandle) {
                        try {
                          const file = await fileHandle.getFile();
                          const lowerName = file.name.toLowerCase();
                          const isImage = isListaImageFile(lowerName);
                          const isEmbeddedSceneImage =
                            lowerName.endsWith(".png") ||
                            lowerName.endsWith(".svg");

                          const settleOpenedFile = async () => {
                            await new Promise<void>((resolve) =>
                              window.setTimeout(resolve, 0),
                            );
                            setAppState(
                              { fileHandle: fileHandle as any } as any,
                              () => {
                                if (isImage || lowerName.endsWith(".pdf")) {
                                  app.setActiveTool({ type: "selection" });
                                }

                                setAppState({
                                  selectedElementIds: {},
                                  selectedGroupIds: {},
                                  selectedLinearElement: null,
                                  editingGroupId: null,
                                  selectionElement: null,
                                  activeEmbeddable: null,
                                  showHyperlinkPopup: false,
                                  suggestedBinding: null,
                                  frameToHighlight: null,
                                } as any);
                              },
                            );
                          };

                          if (isEmbeddedSceneImage) {
                            try {
                              const scene = await loadFromBlob(
                                file,
                                appState,
                                (app as any).scene.getElementsIncludingDeleted(),
                                fileHandle,
                              );

                              app.syncActionResult({
                                ...scene,
                                appState: {
                                  ...(scene.appState || appState),
                                  isLoading: false,
                                },
                                replaceFiles: true,
                                captureUpdate: CaptureUpdateAction.IMMEDIATELY,
                              });
                            } catch (error: any) {
                              if (!(error instanceof ImageSceneDataError)) {
                                throw error;
                              }

                              const { x, y } = (app as any).getViewportCenterSceneCoords();
                              await (app as any).insertImages(
                                [file],
                                x,
                                y,
                                true,
                                false,
                              );
                            }
                          } else if (isImage) {
                            const { x, y } = (
                              app as any
                            ).getViewportCenterSceneCoords();
                            await (app as any).insertImages(
                              [file],
                              x,
                              y,
                              true,
                              false,
                            );
                          } else {
                            await (app as any).loadFileToCanvas(
                              file,
                              fileHandle,
                              undefined,
                              undefined,
                            );
                          }

                          await settleOpenedFile();
                        } catch (error: any) {
                          setAppState({ fileHandle: null } as any);
                          console.error("Falha ao abrir arquivo pelo Tree:", error);
                          (app as any).setState({
                            errorMessage: "Erro ao abrir arquivo: " + error.message,
                          });
                        }
                      }
                    }}
                  >
                    {linesToDraw.map((line, i) => (
                      <div
                        key={`line-${i}`}
                        style={{
                          position: "absolute",
                          left: `calc(${line.d} * 20px + 10px)`,
                          top: line.top,
                          bottom: line.bottom,
                          width: "1px",
                          background: "var(--tree-guide-color)",
                          pointerEvents: "none",
                          zIndex: 0,
                        }}
                      />
                    ))}

                    <span className="onepaper-list-tree__content flex items-center gap-2">
                      {isFolder ? (
                        isExpanded ? (
                          <span className="onepaper-list-tree__icon onepaper-list-tree__icon--folder">
                            {ListaFolderOpenIcon}
                          </span>
                        ) : (
                          <span className="onepaper-list-tree__icon onepaper-list-tree__icon--folder">
                            {ListaFolderIcon}
                          </span>
                        )
                      ) : (() => {
                        const lowerName = item.getItemName().toLowerCase();
                        let IconComponent = ListaFileIcon;
                        let iconClass = "onepaper-list-tree__icon--file";

                        if (lowerName.endsWith(".onepaper") || lowerName.endsWith(".excalidraw")) {
                          IconComponent = FreedrawIcon;
                          iconClass = "onepaper-list-tree__icon--onepaper";
                        } else if (
                          lowerName.endsWith(".onepaper.png") ||
                          lowerName.endsWith(".excalidraw.png") ||
                          lowerName.endsWith(".png")
                        ) {
                          IconComponent = FilePngIcon;
                          iconClass = "onepaper-list-tree__icon--png";
                        } else if (
                          lowerName.endsWith(".onepaper.svg") ||
                          lowerName.endsWith(".excalidraw.svg") ||
                          lowerName.endsWith(".svg")
                        ) {
                          IconComponent = FileSvgIcon;
                          iconClass = "onepaper-list-tree__icon--svg";
                        } else if (lowerName.endsWith(".pdf")) {
                          IconComponent = FilePdfIcon;
                          iconClass = "onepaper-list-tree__icon--pdf";
                        } else if (
                          lowerName.endsWith(".jpg") ||
                          lowerName.endsWith(".jpeg") ||
                          lowerName.endsWith(".gif") ||
                          lowerName.endsWith(".webp")
                        ) {
                          IconComponent = ImageIcon;
                          iconClass = "onepaper-list-tree__icon--image";
                        }

                        return (
                          <span className={`onepaper-list-tree__icon ${iconClass}`}>
                            {IconComponent}
                          </span>
                        );
                      })()}
                      <span className="onepaper-list-tree__label">
                        {isFolder
                          ? item.getItemName()
                          : getListaTreeDisplayName(item.getItemName() || "")}
                      </span>
                    </span>
                  </button>
                  {isFolder && item.getId() !== "root" && (
                    <button
                      type="button"
                      className="onepaper-list-tree__folderAction"
                      aria-label={`Excluir ${item.getItemName()}`}
                      title="Excluir"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeListaTreeFolder(item.getId());
                      }}
                    >
                      {CloseIcon}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <>
        <div
          className="onepaper-explorer-panel__separator"
          style={{ marginTop: 0 }}
        />

        <div style={{ position: "relative" }}>
          <button
            type="button"
            className="onepaper-explorer-panel__item"
            onClick={() => {
              setIsPreferencesOpen(false);
              setIsExtraToolsOpen(false);
              setIsLanguageMenuOpen(false);
              void actionManager.executeAction(actionClearCanvas);
            }}
          >
            <span
              className="onepaper-explorer-panel__itemIcon"
              aria-hidden="true"
            >
              {PlusIcon}
            </span>
            <span className="onepaper-explorer-panel__itemCopy">
              <span className="onepaper-explorer-panel__itemTitle">Novo</span>
            </span>
          </button>
        </div>

        <button
          type="button"
          className="onepaper-explorer-panel__item"
          onClick={() => {
            actionManager.executeAction(actionLoadScene);
          }}
        >
          <span className="onepaper-explorer-panel__itemIcon">{LoadIcon}</span>
          <span className="onepaper-explorer-panel__itemCopy">
            <span className="onepaper-explorer-panel__itemTitle">
              {t("buttons.load")}
            </span>
          </span>
        </button>
        <button
          type="button"
          className="onepaper-explorer-panel__item"
          onClick={() => {
            actionManager.executeAction(actionSaveFileToDisk);
          }}
        >
          <span className="onepaper-explorer-panel__itemIcon">{save}</span>
          <span className="onepaper-explorer-panel__itemCopy">
            <span className="onepaper-explorer-panel__itemTitle">
              {t("buttons.saveAs")}
            </span>
          </span>
        </button>
        <button
          type="button"
          className="onepaper-explorer-panel__item"
          onClick={() => {
            setAppState({ openDialog: { name: "imageExport" } });
          }}
        >
          <span className="onepaper-explorer-panel__itemIcon">
            {ExportImageIcon}
          </span>
          <span className="onepaper-explorer-panel__itemCopy">
            <span className="onepaper-explorer-panel__itemTitle">
              {t("buttons.exportImage")}
            </span>
          </span>
        </button>
        {/* Developer-only entry points stay out of the production surface. */}
        {isDevEnv() && (
          <button
            type="button"
            className="onepaper-explorer-panel__item"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              requestAnimationFrame(() => {
                setAppState({ openDialog: { name: "commandPalette" } });
              });
            }}
          >
            <span className="onepaper-explorer-panel__itemIcon">
              {boltIcon}
            </span>
            <span className="onepaper-explorer-panel__itemCopy">
              <span className="onepaper-explorer-panel__itemTitle">
                {t("commandPalette.title")}
              </span>
            </span>
          </button>
        )}
        <button
          type="button"
          className="onepaper-explorer-panel__item"
          onClick={() => {
            actionManager.executeAction(actionToggleSearchMenu);
          }}
        >
          <span className="onepaper-explorer-panel__itemIcon">
            {searchIcon}
          </span>
          <span className="onepaper-explorer-panel__itemCopy">
            <span className="onepaper-explorer-panel__itemTitle">
              {t("search.title")}
            </span>
          </span>
        </button>
        <button
          type="button"
          className="onepaper-explorer-panel__item"
          onClick={() => {
            actionManager.executeAction(actionShortcuts);
          }}
        >
          <span className="onepaper-explorer-panel__itemIcon">{HelpIcon}</span>
          <span className="onepaper-explorer-panel__itemCopy">
            <span className="onepaper-explorer-panel__itemTitle">
              {t("helpDialog.title")}
            </span>
          </span>
        </button>

        <div className="onepaper-explorer-panel__separator" />

        {isDevEnv() && (
          <button
            type="button"
            className="onepaper-explorer-panel__item"
            onClick={() => {
              window.open(
                `${import.meta.env.VITE_APP_PLUS_APP}${
                  isExcalidrawPlusSignedUser ? "" : "/sign-up"
                }?utm_source=signin&utm_medium=app&utm_content=hamburger`,
                "_blank",
                "noopener noreferrer",
              );
            }}
          >
            <span className="onepaper-explorer-panel__itemIcon">
              {loginIcon}
            </span>
            <span className="onepaper-explorer-panel__itemCopy">
              <span className="onepaper-explorer-panel__itemTitle">
                {isExcalidrawPlusSignedUser ? "Sign in" : "Sign up"}
              </span>
            </span>
          </button>
        )}

        {isDevEnv() && (
          <button
            type="button"
            className="onepaper-explorer-panel__item"
            onClick={() => {
              if (window.visualDebug) {
                delete window.visualDebug;
                saveDebugState({ enabled: false });
              } else {
                window.visualDebug = { data: [] };
                saveDebugState({ enabled: true });
              }
              refresh?.();
            }}
          >
            <span className="onepaper-explorer-panel__itemIcon">{eyeIcon}</span>
            <span className="onepaper-explorer-panel__itemCopy">
              <span className="onepaper-explorer-panel__itemTitle">
                Visual Debug
              </span>
            </span>
          </button>
        )}

        <div
          className="onepaper-explorer-panel__separator"
          style={{ marginTop: 0 }}
        />

        {isDevEnv() && (
          <div style={{ position: "relative" }} ref={preferencesRef}>
            <button
              type="button"
              className="onepaper-explorer-panel__item"
              onClick={() => {
                setIsPreferencesOpen((prev) => !prev);
                setIsExtraToolsOpen(false);
                setIsLanguageMenuOpen(false);
              }}
            >
              <span className="onepaper-explorer-panel__itemIcon">
                {settingsIcon}
              </span>
              <span className="onepaper-explorer-panel__itemCopy">
                <span className="onepaper-explorer-panel__itemTitle">
                  {t("labels.preferences")}
                </span>
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "0.8rem",
                  color: "var(--onepaper-explorer-panel-muted)",
                }}
              >
                &gt;
              </span>
            </button>

            {isPreferencesOpen && (
              <div
                className="onepaper-novo-menu"
                style={{
                  width: "100%",
                  left: 0,
                  top: "auto",
                  bottom: "100%",
                  marginBottom: "0.25rem",
                }}
              >
                <div
                  style={{
                    padding: "0.25rem 0.5rem",
                    fontSize: "0.64rem",
                    textTransform: "uppercase",
                    color: "var(--onepaper-explorer-panel-muted)",
                  }}
                >
                  {t("labels.preferences")}
                </div>

                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() => app.toggleLock()}
                >
                  <span>Keep selected tool active after drawing</span>
                  {appState.activeTool.locked && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() =>
                    actionManager.executeAction(actionToggleObjectsSnapMode)
                  }
                >
                  <span>Snap to objects</span>
                  {appState.objectsSnapModeEnabled && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() =>
                    actionManager.executeAction(actionToggleGridMode)
                  }
                >
                  <span>Show grid</span>
                  {appState.gridModeEnabled && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() =>
                    actionManager.executeAction(actionToggleZenMode)
                  }
                >
                  <span>Zen mode</span>
                  {appState.zenModeEnabled && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() =>
                    actionManager.executeAction(actionToggleViewMode)
                  }
                >
                  <span>View mode</span>
                  {appState.viewModeEnabled && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() => actionManager.executeAction(actionToggleStats)}
                >
                  <span>Stats</span>
                  {appState.stats.open && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() =>
                    actionManager.executeAction(actionToggleArrowBinding)
                  }
                >
                  <span>Bind arrows to objects</span>
                  {appState.bindingPreference === "enabled" && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() =>
                    actionManager.executeAction(actionToggleMidpointSnapping)
                  }
                >
                  <span>Snap arrows to midpoints</span>
                  {appState.isMidpointSnappingEnabled && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {isDevEnv() && (
          <div style={{ position: "relative" }} ref={extraToolsRef}>
            <button
              type="button"
              className="onepaper-explorer-panel__item"
              onClick={() => {
                setIsExtraToolsOpen((prev) => !prev);
                setIsPreferencesOpen(false);
                setIsLanguageMenuOpen(false);
              }}
            >
              <span className="onepaper-explorer-panel__itemIcon">
                {wrenchIcon}
              </span>
              <span className="onepaper-explorer-panel__itemCopy">
                <span className="onepaper-explorer-panel__itemTitle">
                  {t("toolBar.extraTools")}
                </span>
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "0.8rem",
                  color: "var(--onepaper-explorer-panel-muted)",
                }}
              >
                &gt;
              </span>
            </button>

            {isExtraToolsOpen && (
              <div
                className="onepaper-novo-menu"
                style={{
                  width: "100%",
                  left: 0,
                  top: "auto",
                  bottom: "100%",
                  marginBottom: "0.25rem",
                }}
              >
                <div
                  style={{
                    padding: "0.25rem 0.5rem",
                    fontSize: "0.64rem",
                    textTransform: "uppercase",
                    color: "var(--onepaper-explorer-panel-muted)",
                  }}
                >
                  {t("toolBar.extraTools")}
                </div>

                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() => {
                    app.setActiveTool({ type: "embeddable" });
                    setIsExtraToolsOpen(false);
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <span
                      style={{ width: "1rem", height: "1rem", display: "flex" }}
                    >
                      {EmbedIcon}
                    </span>
                    <span>{t("toolBar.embeddable")}</span>
                  </span>
                  {appState.activeTool.type === "embeddable" && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() => {
                    app.setActiveTool({ type: "lasso" });
                    setIsExtraToolsOpen(false);
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <span
                      style={{ width: "1rem", height: "1rem", display: "flex" }}
                    >
                      {LassoIcon}
                    </span>
                    <span>{t("toolBar.lasso")}</span>
                  </span>
                  {appState.activeTool.type === "lasso" && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>

                <div
                  style={{
                    padding: "0.25rem 0.5rem",
                    fontSize: "0.64rem",
                    textTransform: "uppercase",
                    color: "var(--onepaper-explorer-panel-muted)",
                    marginTop: "0.5rem",
                  }}
                >
                  Generate
                </div>

                <button
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={() => {
                    app.setOpenDialog({ name: "ttd", tab: "mermaid" });
                    setIsExtraToolsOpen(false);
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <span
                      style={{ width: "1rem", height: "1rem", display: "flex" }}
                    >
                      {mermaidLogoIcon}
                    </span>
                    <span>{t("toolBar.mermaidToExcalidraw")}</span>
                  </span>
                </button>

                {appProps.aiEnabled !== false && app.plugins?.diagramToCode && (
                  <button
                    type="button"
                    className="onepaper-novo-menu__item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                    onClick={() => {
                      app.onMagicframeToolSelect();
                      setIsExtraToolsOpen(false);
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <span
                        style={{
                          width: "1rem",
                          height: "1rem",
                          display: "flex",
                        }}
                      >
                        {MagicIcon}
                      </span>
                      <span>{t("toolBar.magicframe")}</span>
                    </span>
                    <span
                      style={{
                        fontSize: "0.55rem",
                        padding: "0.1rem 0.3rem",
                        background: "var(--color-primary)",
                        color: "white",
                        borderRadius: "4px",
                        fontWeight: "bold",
                      }}
                    >
                      AI
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div
          className="onepaper-explorer-panel__custom-block"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0.4rem 0.6rem",
          }}
        >
          <span className="onepaper-explorer-panel__itemTitle">
            {t("labels.theme")}
          </span>
          <div
            className="onepaper-theme-toggle"
            style={{
              display: "flex",
              gap: "0.2rem",
              padding: "0.15rem",
              background: "var(--onepaper-explorer-panel-bg)",
              borderRadius: "1rem",
              border: "1px solid var(--onepaper-explorer-panel-border)",
            }}
          >
            <button
              type="button"
              onClick={() => appProps.onThemeChange?.(THEME.LIGHT)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  appState.theme === THEME.LIGHT
                    ? "var(--color-primary, #6965db)"
                    : "transparent",
                color:
                  appState.theme === THEME.LIGHT
                    ? "#fff"
                    : "var(--icon-fill-color)",
                border: "none",
                padding: "0.25rem 0.6rem",
                borderRadius: "0.8rem",
                cursor: "pointer",
              }}
            >
              <div style={{ width: "1rem", height: "1rem" }}>{SunIcon}</div>
            </button>
            <button
              type="button"
              onClick={() => appProps.onThemeChange?.(THEME.DARK)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  appState.theme === THEME.DARK
                    ? "var(--color-primary, #6965db)"
                    : "transparent",
                color:
                  appState.theme === THEME.DARK
                    ? "#fff"
                    : "var(--icon-fill-color)",
                border: "none",
                padding: "0.25rem 0.6rem",
                borderRadius: "0.8rem",
                cursor: "pointer",
              }}
            >
              <div style={{ width: "1rem", height: "1rem" }}>{MoonIcon}</div>
            </button>
          </div>
        </div>

        {isDevEnv() && (
          <div
            className="onepaper-explorer-panel__custom-block"
            ref={devicePreviewMenuRef}
            style={{ position: "relative" }}
          >
            <button
              type="button"
              className="onepaper-explorer-panel__item"
              style={{ justifyContent: "space-between" }}
              onClick={() => {
                setIsDevicePreviewMenuOpen((prev) => !prev);
                setIsLanguageMenuOpen(false);
                setIsPreferencesOpen(false);
                setIsExtraToolsOpen(false);
              }}
            >
              <span className="onepaper-explorer-panel__itemCopy">
                <span className="onepaper-explorer-panel__itemTitle">
                  Visual da tela
                </span>
                <span className="onepaper-explorer-panel__itemSubtitle">
                  {getDevicePreviewLabel(devicePreviewMode)}
                </span>
              </span>
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "var(--onepaper-explorer-panel-muted)",
                  transform: isDevicePreviewMenuOpen
                    ? "rotate(180deg)"
                    : "none",
                  transition: "transform 150ms ease",
                }}
              >
                â–¼
              </span>
            </button>

            {isDevicePreviewMenuOpen && (
              <div
                className="onepaper-novo-menu"
                style={{
                  width: "100%",
                  left: 0,
                  top: "auto",
                  bottom: "100%",
                  marginBottom: "0.25rem",
                  maxHeight: "12rem",
                }}
              >
                {[
                  {
                    mode: "desktop" as const,
                    label: "Computador",
                    icon: DeviceDesktopIcon,
                  },
                  {
                    mode: "tablet" as const,
                    label: "Tablet",
                    icon: DeviceTabletIcon,
                  },
                  {
                    mode: "phone" as const,
                    label: "Celular",
                    icon: DevicePhoneIcon,
                  },
                ].map(({ mode, label, icon }) => (
                  <button
                    key={mode}
                    type="button"
                    className="onepaper-novo-menu__item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background:
                        mode === devicePreviewMode
                          ? "var(--onepaper-explorer-panel-surface-hover)"
                          : "transparent",
                    }}
                    onClick={() => {
                      onDevicePreviewModeChange(mode);
                      setIsDevicePreviewMenuOpen(false);
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <span
                        style={{
                          width: "1rem",
                          height: "1rem",
                          display: "flex",
                        }}
                      >
                        {icon}
                      </span>
                      <span>{label}</span>
                    </span>
                    {mode === devicePreviewMode && (
                      <span style={{ width: "0.8rem", height: "0.8rem" }}>
                        {checkIcon}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div
          className="onepaper-explorer-panel__custom-block"
          ref={languageMenuRef}
          style={{ position: "relative" }}
        >
          <button
            type="button"
            className="onepaper-explorer-panel__item"
            style={{ justifyContent: "space-between" }}
            onClick={() => {
              setIsLanguageMenuOpen((prev) => !prev);
              setIsPreferencesOpen(false);
              setIsExtraToolsOpen(false);
            }}
          >
            <span>
              {languages.find((l) => l.code === langCode)?.label ||
                t("buttons.selectLanguage")}
            </span>
            <span
              style={{
                fontSize: "0.7rem",
                color: "var(--onepaper-explorer-panel-muted)",
                transform: isLanguageMenuOpen ? "rotate(180deg)" : "none",
                transition: "transform 150ms ease",
              }}
            >
              ▼
            </span>
          </button>

          {isLanguageMenuOpen && (
            <div
              className="onepaper-novo-menu"
              style={{
                width: "100%",
                left: 0,
                top: "auto",
                bottom: "100%",
                marginBottom: "0.25rem",
                maxHeight: "15rem",
                overflowY: "auto",
              }}
            >
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  className="onepaper-novo-menu__item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background:
                      lang.code === langCode
                        ? "var(--onepaper-explorer-panel-surface-hover)"
                        : "transparent",
                  }}
                  onClick={() => {
                    setLangCode(lang.code);
                    setIsLanguageMenuOpen(false);
                  }}
                >
                  <span>{lang.label}</span>
                  {lang.code === langCode && (
                    <span style={{ width: "0.8rem", height: "0.8rem" }}>
                      {checkIcon}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div
          className="onepaper-explorer-panel__custom-block"
          style={{ marginTop: "0.5rem" }}
        >
          <div
            className="onepaper-explorer-panel__itemTitle"
            style={{ marginBottom: "0.4rem" }}
          >
            {t("labels.canvasBackground")}
          </div>
          <div>{renderExplorerAction("changeViewBackgroundColor")}</div>
        </div>
      </>
    );
  };

  return (
    <>
      <DefaultSidebar>
        <DefaultSidebar.TabTriggers>
          <Sidebar.TabTrigger tab="miniature-panel" title="Painel de Navegação">
            {gridIcon}
          </Sidebar.TabTrigger>
        </DefaultSidebar.TabTriggers>
        <Sidebar.Tab
          tab="miniature-panel"
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MiniaturePanel />
        </Sidebar.Tab>
      </DefaultSidebar>

      <div
        ref={sidebarRef}
        className={clsx("onepaper-activity-shell", {
          "onepaper-activity-shell--open": isExplorerPanelOpen,
        })}
      >
        <div
          className="onepaper-activity-bar"
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            type="button"
            className="onepaper-activity-bar__button explorer-button"
            title="Explorer"
            aria-label="Explorer"
            aria-pressed={isExplorerViewActive}
            onClick={() => {
              // When the properties panel owns the sidebar, this path lets the user
              // return to the general explorer without losing the active selection context.
              if (isPropertiesPanelOpen) {
                setForceMenu(true);
                setActivePanel("explorer");
                // Reseta a ferramenta ativa e tira o foco
                setAppState({
                  activeTool: {
                    ...appState.activeTool,
                    type: "selection",
                    customType: null,
                  },
                  selectedElementIds: {},
                });
              } else {
                setActivePanel((value) =>
                  value === "explorer" ? null : "explorer",
                );
              }
            }}
          >
            {ExplorerIcon}
          </button>

          {devicePreviewMode !== "phone" && (
            <button
              type="button"
              className="onepaper-activity-bar__button list-button"
              title="Lista"
              aria-label="Lista"
              aria-pressed={isListaViewActive}
              onClick={() => {
                if (isPropertiesPanelOpen) {
                  setForceMenu(true);
                }

                setActivePanel((value) => (value === "lista" ? null : "lista"));
              }}
            >
              {ListIcon}
            </button>
          )}

          <div className="onepaper-activity-bar__shapes">
            <LockButton
              checked={appState.activeTool.locked}
              onChange={() => {
                setAppState({
                  activeTool: {
                    ...appState.activeTool,
                    locked: !appState.activeTool.locked,
                  },
                });
              }}
              title="Manter ferramenta selecionada"
            />
            <ShapesSwitcher
              setAppState={setAppState}
              activeTool={appState.activeTool}
              UIOptions={appProps.UIOptions}
              app={app as any}
              extraToolsMenuSide="right"
              radioGroupName="sidebar-current-shape"
            />
          </div>

          <button
            type="button"
            className="onepaper-activity-bar__button"
            style={{ marginTop: "auto", marginBottom: "0.5rem" }}
            title={t("helpDialog.title")}
            aria-label={t("helpDialog.title")}
            onClick={() => actionManager.executeAction(actionShortcuts)}
          >
            {HelpIcon}
          </button>
        </div>

        <aside
          ref={explorerPanelRef}
          className="onepaper-explorer-panel"
          aria-hidden={!isExplorerPanelOpen}
        >
          <header className="onepaper-explorer-panel__header">
            <div className="onepaper-explorer-panel__titleGroup">
              <span className="onepaper-explorer-panel__eyebrow">
                {isListaViewActive ? "Lista" : "Explorer"}
              </span>
              <strong className="onepaper-explorer-panel__title">
                OnePaper
              </strong>
            </div>
            {isListaViewActive && (
              <div className="onepaper-explorer-panel__actions">
                <button
                  type="button"
                  className="onepaper-explorer-panel__action"
                  title="Adicionar Pasta"
                  aria-label="Adicionar Pasta"
                  onClick={async () => {
                    try {
                      const dirHandle = await (
                        window as any
                      ).showDirectoryPicker();
                      const id = `root/${dirHandle.name}`;

                      const nextState = {
                        ...treeItemsRef.current,
                        [id]: {
                          name: dirHandle.name,
                          handle: dirHandle,
                          children: [],
                          isLoaded: false,
                        },
                        root: {
                          ...treeItemsRef.current.root,
                          children: [
                            ...(treeItemsRef.current.root.children || []),
                            id,
                          ],
                        },
                      };

                      if (treeItemsRef.current.root.children?.includes(id)) {
                        return;
                      }

                      treeItemsRef.current = nextState;
                      setTreeItems(nextState);
                      // Seed cache do item novo e atualiza filhos do root sem esperar reload.
                      tree
                        .getItemInstance(id)
                        .updateCachedData(nextState[id], true);
                      syncListaTreeRoot(nextState.root.children ?? []);
                      tree.getItemInstance(id).expand();
                    } catch (e) {
                      console.error("Error picking directory", e);
                    }
                  }}
                >
                  {ListaAddFolderIcon}
                </button>
                <button
                  type="button"
                  className="onepaper-explorer-panel__action"
                  title="Atualizar"
                  aria-label="Atualizar"
                  onClick={async () => {
                    await refreshListaTree();
                    refresh?.();
                  }}
                >
                  {ListaRefreshFolderIcon}
                </button>
              </div>
            )}
          </header>

          <div className="onepaper-explorer-panel__content">
            {renderExplorerContent()}
          </div>
        </aside>
      </div>
    </>
  );
};
